import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';
import FormField from '../common/FormField';
import { useLanguage } from '../../assets/js/LanguageContext';

const pickMimeType = () => {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/wav'];
  return candidates.find((type) => window.MediaRecorder && MediaRecorder.isTypeSupported(type)) || 'audio/webm';
};

const OpenQuestion = ({ question, value = '', onChange, label }) => {
  const { t } = useLanguage();
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [message, setMessage] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const valueRef = useRef(value);
  const messageTimer = useRef(null);

  valueRef.current = value;

  useEffect(() => () => {
    clearTimeout(messageTimer.current);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.onstop = null;
      recorderRef.current.stop();
    }
    if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
  }, []);

  const flash = (text) => {
    setMessage(text);
    clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(null), 4000);
  };

  const transcribe = async (blob, mimeType) => {
    setTranscribing(true);
    const extension = mimeType.includes('mp4') ? 'm4a' : mimeType.includes('wav') ? 'wav' : 'webm';
    const formData = new FormData();
    formData.append('file', blob, `recording_${Date.now()}.${extension}`);
    try {
      const response = await fetch(`${process.env.REACT_APP_AI_URL}/upload-audio`, { method: 'POST', body: formData });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      const text = (data.transcription || '').trim();
      if (!text) {
        flash(t('audio_not_detected_text'));
        return;
      }
      const current = (valueRef.current || '').trim();
      onChange(current ? `${current} ${text}` : text);
    } catch (error) {
      console.error('Error al transcribir el audio:', error);
      flash(t('audio_transcription_error'));
    } finally {
      setTranscribing(false);
    }
  };

  const startRecording = async () => {
    setMessage(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 }
      });
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        transcribe(new Blob(chunksRef.current, { type: mimeType }), mimeType);
      };
      recorder.start(1000);
      recorderRef.current = recorder;
      streamRef.current = stream;
      setRecording(true);
    } catch (error) {
      console.error('Error al iniciar la grabación:', error);
      flash(t('audio_recording_start_error'));
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
    setRecording(false);
  };

  return (
    <div className="tp-form tp-form--bare tp-answer__open">
      <FormField
        id={`respuesta-${question.id}`}
        label={label}
        hideLabel
        as="textarea"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('survey_your_answer')}
        disabled={transcribing}
        rows={5}
      />
      <div className="tp-answer__dictate">
        <button
          type="button"
          className={`tp-answer__mic ${recording ? 'is-recording' : ''}`.trim()}
          onClick={recording ? stopRecording : startRecording}
          disabled={transcribing}
          aria-pressed={recording}
        >
          {recording
            ? <Square size={13} strokeWidth={2.5} aria-hidden="true" />
            : <Mic size={16} strokeWidth={2} aria-hidden="true" />}
          {recording ? t('answer_dictate_stop') : t('answer_dictate')}
        </button>
        <span className="tp-answer__dictate-status" role="status" aria-live="polite">
          {recording && (
            <>
              <span className="tp-answer__rec-dot" aria-hidden="true" />
              {t('answer_recording')}
            </>
          )}
          {transcribing && t('audio_transcribing')}
          {!recording && !transcribing && message}
        </span>
      </div>
    </div>
  );
};

export default OpenQuestion;
